import { useState } from 'react';
import { SectionToggle } from '../SectionToggle';
import { ElasticSchemesEditor } from './elastics/ElasticSchemesEditor';
import { LegacyElasticsForm } from './elastics/LegacyElasticsForm';
import type { ElasticScheme, LegacyElastics } from '@odo/shared-ui/clinical/elastics/model';
import {
    createElasticScheme,
    hasLegacyElastics,
    schemesHaveElastics
} from '@odo/shared-ui/clinical/elastics/persistence';

interface ClinicalElasticsStepProps {
    schemes: ElasticScheme[];
    onChange: (next: ElasticScheme[]) => void;
    /** Legacy per-side values, present only for pre-odontogram records. */
    legacyElastics?: LegacyElastics;
    onLegacyChange: (next: LegacyElastics) => void;
    /** When false, the section is omitted from the generated PDF. */
    enabled: boolean;
    onToggle: () => void;
}

/**
 * Dedicated wizard step for elastics. New records build one or more schemes:
 * each scheme pins an aligner range (e.g. 1 → 5), a daily wear time, and its
 * own odontogram placements; add further schemes for later aligner spans.
 * Records saved before the odontogram open in the old Left/Right form (still
 * editable); the doctor can switch to the chart to migrate them. The whole
 * section can be toggled off (omitted from the PDF) via the header switch.
 */
function ClinicalElasticsStep({
    schemes,
    onChange,
    legacyElastics,
    onLegacyChange,
    enabled,
    onToggle
}: ClinicalElasticsStepProps) {
    const legacyAvailable = hasLegacyElastics(legacyElastics);
    const isLegacyRecord = schemes.length === 0 && legacyAvailable;
    // Start on the old form only for a pure-legacy record; otherwise the chart.
    const [showChart, setShowChart] = useState(!isLegacyRecord);

    // Seed the first scheme when the doctor turns the section on for a fresh
    // record, so the chart is ready to draw into. Done on the enable action (not
    // reactively) so deleting the last scheme leaves an empty state instead of
    // instantly re-seeding one.
    const handleToggle = () => {
        if (!enabled && showChart && !legacyAvailable && schemes.length === 0) {
            onChange([createElasticScheme()]);
        }
        onToggle();
    };

    // Switching a legacy record to the chart also seeds a scheme to migrate into.
    const handleSwitchToChart = () => {
        setShowChart(true);
        if (schemes.length === 0) onChange([createElasticScheme()]);
    };

    return (
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-800">Elastics placement</h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                        {showChart ? (
                            <>
                                Set the aligner range and daily wear for each schema, then click{' '}
                                <span className="font-semibold">Add elastic</span> and tap teeth in
                                order - tap the first tooth again to close the band. Add another
                                schema when the wear or placement changes later in treatment.
                            </>
                        ) : (
                            <>
                                This record uses the old Left/Right elastics format. Edit it below,
                                or switch to the odontogram chart to migrate it.
                            </>
                        )}
                    </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                    <SectionToggle enabled={enabled} onToggle={handleToggle} />
                </div>
            </div>

            <div className={enabled ? undefined : 'pointer-events-none select-none opacity-40'}>
                {showChart ? (
                    <div className="flex flex-col gap-3">
                        {legacyAvailable && !schemesHaveElastics(schemes) && (
                            <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                                <span className="text-xs text-amber-800">
                                    Migrating from the old format. Place elastics on the chart to
                                    replace the old Left/Right values, or go back to keep them.
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowChart(false)}
                                    className="flex-shrink-0 text-xs font-semibold text-amber-800 underline hover:text-amber-900"
                                >
                                    Back to old form
                                </button>
                            </div>
                        )}
                        {enabled && !legacyAvailable && !schemesHaveElastics(schemes) && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                                <span className="text-xs text-amber-800">
                                    Add at least one elastic before submitting, or turn this section
                                    off to omit it from the PDF.
                                </span>
                            </div>
                        )}
                        <ElasticSchemesEditor schemes={schemes} onChange={onChange} />
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        <LegacyElasticsForm value={legacyElastics} onChange={onLegacyChange} />
                        <div>
                            <button
                                type="button"
                                onClick={handleSwitchToChart}
                                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                            >
                                Switch to odontogram chart
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export { ClinicalElasticsStep };
